/**
 * Types for org_navigation table (nav items for multi-page public websites).
 * @see supabase/migrations/20260302064000_navigation_data_model.sql
 */

export interface NavItem {
  id: string
  org_id: string
  label: string
  href: string
  order_index: number
  open_in_new_tab: boolean
  /** NOS-188: one level of nesting — child items render in a dropdown under their parent */
  parent_id?: string | null
  created_at: string
  updated_at: string
}

export interface NavItemInsert {
  org_id: string
  label: string
  href: string
  order_index?: number
  open_in_new_tab?: boolean
  parent_id?: string | null
}

export interface NavItemUpdate {
  label?: string
  href?: string
  order_index?: number
  open_in_new_tab?: boolean
  parent_id?: string | null
}

/** A top-level nav item with its dropdown children (NOS-188). */
export interface NavTreeNode extends NavItem {
  children: NavItem[]
}

/**
 * NOS-215: the optional SECONDARY nav CTA (outline button — e.g. an imported
 * "Subscribe") is stored as a SELF-PARENTED org_navigation row
 * (parent_id = id). The convention is additive: no real nav row ever
 * self-references, buildNavTree excludes it from the link tree, and
 * PublicNavBar looks it up explicitly to render the outline button next to
 * the primary (Donate) CTA. Fragment hrefs ("#footer-email-optin") scroll to
 * the matching anchor, or the page end when it doesn't exist.
 */
export function isSecondaryNavCta(item: NavItem): boolean {
  return item.parent_id != null && item.parent_id === item.id
}

/** First self-parented row = the secondary nav CTA, or null when unset. */
export function findSecondaryNavCta(items: NavItem[]): NavItem | null {
  return items.find(isSecondaryNavCta) ?? null
}

/**
 * Group flat org_navigation rows into a one-level tree: top-level items in
 * order_index order, each with its children (also order_index ordered).
 *
 * NOS-199: an "orphan" — a row whose parent_id points at an item not in the
 * set — is promoted to top level deliberately (never silently dropped) so a
 * data glitch degrades to a visible flat link rather than a vanished one. The
 * import pipeline now guarantees orphans are never written; if one still shows
 * up (legacy data, manual edit), we warn so the reshaping is observable rather
 * than silent.
 */
export function buildNavTree(rows: NavItem[]): NavTreeNode[] {
  // NOS-215: self-parented rows are the secondary-CTA slot, not nav links.
  const items = rows.filter((i) => !isSecondaryNavCta(i))
  const ids = new Set(items.map((i) => i.id))
  const byOrder = (a: NavItem, b: NavItem) => a.order_index - b.order_index
  const orphans = items.filter((i) => i.parent_id && !ids.has(i.parent_id))
  if (orphans.length > 0 && typeof console !== "undefined") {
    console.warn(
      `buildNavTree: ${orphans.length} nav item(s) reference a missing parent and were promoted to top level`,
      orphans.map((o) => o.id)
    )
  }
  const topLevel = items
    .filter((i) => !i.parent_id || !ids.has(i.parent_id))
    .sort(byOrder)
  return topLevel.map((item) => ({
    ...item,
    children: items.filter((c) => c.parent_id === item.id).sort(byOrder),
  }))
}
